import React, { useState, useEffect, useCallback } from 'react';
import SmartTable, { SmartTableColumn } from '../../../../shared/components/SmartTable';
import SmartTableButtons from '../../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../../shared/hooks/useAuth';
import api from '../../../../shared/utils/api';
import ErrorPopup from '../../../../shared/components/ErrorPopup';
import WarningPopup from '../../../../shared/components/WarningPopup';

interface InsuranceHistoryCustomer {
  customerOid: string;
  customerID: string;
  customerName: string;
  empID: string;
  insuranceLoginId: string;
  year: number;
  history: {
    totalRecords: number;
    totalSalary: number;
    totalInvoiceAmount: number;
    totalPaidAmount: number;
    totalRemainingAmount: number;
    paidRecords: number;
    unpaidRecords: number;
    partiallyPaidRecords: number;
  } | null;
  months: Array<{
    id: string;
    year: number;
    month: number;
    monthName: string;
    totalSalary: number;
    totalInsured: number;
    invoiceAmount: number;
    paidAmount: number;
    invoiceBalance: string | null;
    remainingAmount: number;
    invoiceStatus: string;
    statusText: string;
    reportType: string;
    employerId: number;
    empId: number;
    isPaid: boolean;
    isPartiallyPaid: boolean;
  }> | null;
  error?: string;
}

interface InsuranceHistoryResponse {
  success: boolean;
  message: string;
  data: {
    accountantOid: string;
    year: number;
    summary: {
      totalCustomers: number;
      totalRecords: number;
      totalSalary: number;
      totalInvoiceAmount: number;
      totalPaidAmount: number;
      totalRemainingAmount: number;
      paidRecords: number;
      unpaidRecords: number;
      partiallyPaidRecords: number;
    };
    customers: InsuranceHistoryCustomer[];
  };
}

const InsuranceGetHistoryReport: React.FC = () => {
  const { userAuth } = useAuth();
  const [insuranceHistories, setInsuranceHistories] = useState<InsuranceHistoryCustomer[]>([]);
  const [summary, setSummary] = useState<InsuranceHistoryResponse['data']['summary'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedCustomer, setSelectedCustomer] = useState<string>('Бүгд'); // 'Бүгд' means 'All'
  const [customers, setCustomers] = useState<Array<{ customerOid: string; customerID: string; customerName: string }>>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const fetchInsuranceHistories = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔥 InsuranceGetHistoryReport: Starting to fetch insurance histories...');
      console.log('🔥 Selected year:', selectedYear);
      console.log('🔥 Selected customer:', selectedCustomer);
      
      // AccountantOid авах
      const accountantOid = userAuth.accountantOid || localStorage.getItem('accountantOid');
      
      if (!accountantOid) {
        setErrorMessage('AccountantOid олдсонгүй. Дахин нэвтэрнэ үү.');
        setShowErrorPopup(true);
        setLoading(false);
        return;
      }

      console.log('🔍 AccountantOid:', accountantOid);
      
      let result: any;
      
      // If "Бүгд" (All) is selected, use get-customers-history API
      if (selectedCustomer === 'Бүгд') {
        const queryParams = new URLSearchParams({
          accountantOid: accountantOid,
          year: selectedYear
        });
        const apiUrl = `/api/insurance/get-customers-history?${queryParams.toString()}`;
        
        console.log('🌐 API URL (All customers):', apiUrl);
        
        result = await api.get(apiUrl);
        
        if (result && result.success && result.data) {
          const { customers: fetchedCustomers, summary } = result.data;
          
          console.log(`✅ Fetched ${fetchedCustomers.length} customers with insurance history`);
          console.log('📊 Summary:', summary);
          
          setInsuranceHistories(fetchedCustomers);
          setSummary(summary);
          
          // Extract unique customers for dropdown
          const uniqueCustomers = fetchedCustomers.reduce((acc: any[], customer: InsuranceHistoryCustomer) => {
            if (!acc.find(c => c.customerOid === customer.customerOid)) {
              acc.push({
                customerOid: customer.customerOid,
                customerID: customer.customerID,
                customerName: customer.customerName
              });
            }
            return acc;
          }, []);
          setCustomers(uniqueCustomers);
          
          // Тайлангийн түүх олдохгүй үед warning message харуулах
          if (fetchedCustomers.length === 0) {
            setWarningMessage('НДШ тайлангийн түүх олдсонгүй');
            setShowWarningAlert(true);
          } else {
            setShowWarningAlert(false);
          }
        } else {
          setErrorMessage('Тайлангийн түүх авахад алдаа гарлаа');
          setShowErrorPopup(true);
          setInsuranceHistories([]);
          setSummary(null);
        }
      } else {
        // Single customer selected - use get-customers-history API and filter by customerOID
        // This API returns detailed months data, not just summary
        const queryParams = new URLSearchParams({
          accountantOid: accountantOid,
          year: selectedYear
        });
        const apiUrl = `/api/insurance/get-customers-history?${queryParams.toString()}`;
        
        console.log('🌐 API URL (Single customer - filtering):', apiUrl);
        
        result = await api.get(apiUrl);
        
        console.log('✅ API Response:', result);
        console.log('✅ API Response data:', result?.data);
        
        if (result && result.success && result.data) {
          const { customers: allCustomers, summary } = result.data;
          
          console.log(`✅ Fetched ${allCustomers.length} customers, filtering for customerOID: ${selectedCustomer}`);
          
          // Filter customers by selected customerOID
          const selectedCustomerData = allCustomers.find((c: InsuranceHistoryCustomer) => c.customerOid === selectedCustomer);
          
          if (selectedCustomerData) {
            console.log('✅ Found customer data:', selectedCustomerData);
            
            // Extract unique customers for dropdown if not already populated
            if (customers.length === 0) {
              const uniqueCustomers = allCustomers.reduce((acc: any[], customer: InsuranceHistoryCustomer) => {
                if (!acc.find(c => c.customerOid === customer.customerOid)) {
                  acc.push({
                    customerOid: customer.customerOid,
                    customerID: customer.customerID,
                    customerName: customer.customerName
                  });
                }
                return acc;
              }, []);
              setCustomers(uniqueCustomers);
            }
            
            // Set only the selected customer
            setInsuranceHistories([selectedCustomerData]);
            setSummary(summary);
            
            // Check if customer has months data
            if (!selectedCustomerData.months || selectedCustomerData.months.length === 0) {
              setWarningMessage('НДШ тайлангийн түүх олдсонгүй');
              setShowWarningAlert(true);
            } else {
              setShowWarningAlert(false);
            }
          } else {
            console.warn('⚠️ Customer not found in response:', selectedCustomer);
            setWarningMessage('Сонгосон харилцагчийн өгөгдөл олдсонгүй');
            setShowWarningAlert(true);
            setInsuranceHistories([]);
            setSummary(null);
          }
        } else {
          setErrorMessage('Тайлангийн түүх авахад алдаа гарлаа');
          setShowErrorPopup(true);
          setInsuranceHistories([]);
          setSummary(null);
        }
      }
    } catch (error: any) {
      console.error('❌ Error fetching insurance histories:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'НДШ тайлангийн түүх авахад алдаа гарлаа';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint олдсонгүй. Сервертэй холбогдож чадсангүй.';
        setWarningMessage('НДШ тайлангийн түүх олдсонгүй');
        setShowWarningAlert(true);
      } else if (error.response?.status === 401) {
        errorMessage = 'Нэвтрэх эрх шаардлагатай. Дахин нэвтэрнэ үү.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Серверийн алдаа. Дахин оролдоно уу.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Сүлжээний алдаа. Сервер ажиллаж байгаа эсэхийг шалгана уу.';
      }
      
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
      setInsuranceHistories([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedCustomer, userAuth.accountantOid]);

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    fetchInsuranceHistories();
  };

  // Flatten months array into individual rows
  const flattenMonthsData = (): any[] => {
    const flattenedData: any[] = [];
    
    insuranceHistories.forEach((customer) => {
      if (customer.months && customer.months.length > 0) {
        customer.months.forEach((month: any) => {
          flattenedData.push({
            // Unique key for row
            _rowKey: `${customer.customerOid}-${month.id || `${month.year}-${month.month}` || Math.random()}`,
            // Customer info
            customerOid: customer.customerOid,
            customerID: customer.customerID,
            customerName: customer.customerName,
            empID: customer.empID,
            insuranceLoginId: customer.insuranceLoginId,
            year: customer.year,
            // Month info
            id: month.id,
            monthYear: month.year,
            month: month.month,
            monthName: month.monthName,
            totalSalary: month.totalSalary,
            totalInsured: month.totalInsured,
            invoiceAmount: month.invoiceAmount,
            paidAmount: month.paidAmount,
            invoiceBalance: month.invoiceBalance,
            remainingAmount: month.remainingAmount,
            invoiceStatus: month.invoiceStatus,
            statusText: month.statusText,
            reportType: month.reportType,
            digitalSign: month.digitalSign,
            cancelReason: month.cancelReason,
            rowNo: month.rowNo,
            employerId: month.employerId,
            empId: month.empId,
            isPaid: month.isPaid,
            isPartiallyPaid: month.isPartiallyPaid,
          });
        });
      } else {
        // Хэрэв months байхгүй бол customer-ийн мэдээлэл л харуулах
        flattenedData.push({
          // Unique key for row
          _rowKey: `${customer.customerOid}-empty`,
          customerOid: customer.customerOid,
          customerID: customer.customerID,
          customerName: customer.customerName,
          empID: customer.empID,
          insuranceLoginId: customer.insuranceLoginId,
          year: customer.year,
          // Month info - хоосон
          id: null,
          monthYear: null,
          month: null,
          monthName: null,
          totalSalary: null,
          totalInsured: null,
          invoiceAmount: null,
          paidAmount: null,
          invoiceBalance: null,
          remainingAmount: null,
          invoiceStatus: null,
          statusText: null,
          reportType: null,
          digitalSign: null,
          cancelReason: null,
          rowNo: null,
          employerId: null,
          empId: null,
          isPaid: null,
          isPartiallyPaid: null,
        });
      }
    });
    
    return flattenedData;
  };

  // Calculate footer data with aggregations
  const calculateFooterData = (): any => {
    const data = flattenMonthsData();
    
    if (data.length === 0) {
      return null;
    }

    // Calculate sums for numeric columns
    const sums = {
      totalSalary: 0,
      totalInsured: 0,
      invoiceAmount: 0,
      paidAmount: 0,
      remainingAmount: 0,
      invoiceBalance: 0,
    };

    data.forEach((row) => {
      if (row.totalSalary !== null && row.totalSalary !== undefined && !isNaN(row.totalSalary)) {
        sums.totalSalary += parseFloat(row.totalSalary);
      }
      if (row.totalInsured !== null && row.totalInsured !== undefined && !isNaN(row.totalInsured)) {
        sums.totalInsured += parseFloat(row.totalInsured);
      }
      if (row.invoiceAmount !== null && row.invoiceAmount !== undefined && !isNaN(row.invoiceAmount)) {
        sums.invoiceAmount += parseFloat(row.invoiceAmount);
      }
      if (row.paidAmount !== null && row.paidAmount !== undefined && !isNaN(row.paidAmount)) {
        sums.paidAmount += parseFloat(row.paidAmount);
      }
      if (row.remainingAmount !== null && row.remainingAmount !== undefined && !isNaN(row.remainingAmount)) {
        sums.remainingAmount += parseFloat(row.remainingAmount);
      }
      if (row.invoiceBalance !== null && row.invoiceBalance !== undefined) {
        const balanceValue = typeof row.invoiceBalance === 'string' ? parseFloat(row.invoiceBalance) : row.invoiceBalance;
        if (!isNaN(balanceValue)) {
          sums.invoiceBalance += balanceValue;
        }
      }
    });

    return {
      customerID: `Нийт: ${data.length}`,
      customerName: '',
      empID: '',
      monthYear: '',
      monthName: '',
      totalSalary: sums.totalSalary,
      totalInsured: sums.totalInsured,
      invoiceAmount: sums.invoiceAmount,
      paidAmount: sums.paidAmount,
      remainingAmount: sums.remainingAmount,
      invoiceBalance: sums.invoiceBalance,
      statusText: '',
      reportType: '',
      isPaid: null,
    };
  };

  // Create columns for flattened months data
  const getColumns = (): SmartTableColumn[] => [
    { 
      key: 'customerID', 
      label: 'Харилцагчийн регистр',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter && typeof value === 'string' && value.startsWith('Нийт:')) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value}</div>;
        }
        return value || '-';
      }
    },
    { key: 'customerName', label: 'Харилцагчийн нэр' },
    { key: 'empID', label: 'Ажил олгогчийн ID' },
    { 
      key: 'monthYear', 
      label: 'Жил',
      render: (value: any) => value || '-'
    },
    { 
      key: 'monthName', 
      label: 'Сар',
      render: (value: any) => value || '-'
    },
    { 
      key: 'totalSalary', 
      label: 'Нийт цалин',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const formatted = value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    { 
      key: 'totalInsured', 
      label: 'Нийт даатгуулсан',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value.toLocaleString('mn-MN')}</div>;
        }
        return value;
      }
    },
    { 
      key: 'invoiceAmount', 
      label: 'Төлөх шимтгэл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const formatted = value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    { 
      key: 'paidAmount', 
      label: 'Төлсөн дүн',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const formatted = value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        if (isFooter) {
          return <div style={{ color: '#10B981', fontWeight: 'bold' }}>{formatted}</div>;
        }
        return <div style={{ color: '#10B981', fontWeight: 'bold' }}>{formatted}</div>;
      }
    },
    { 
      key: 'remainingAmount', 
      label: 'Үлдэгдэл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const formatted = value.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const color = value > 0 ? '#DC2626' : '#10B981';
        return <div style={{ color, fontWeight: 'bold' }}>{formatted}</div>;
      }
    },
    { 
      key: 'invoiceBalance', 
      label: 'Төлөх үлдэгдэл',
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return value;
        const formatted = numValue.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const color = numValue > 0 ? '#DC2626' : '#10B981';
        return <div style={{ color, fontWeight: 'bold' }}>{formatted}</div>;
      }
    },
    { 
      key: 'statusText', 
      label: 'Төлөв',
      render: (value: any, row: any) => {
        if (!value && !row.invoiceStatus) return '-';
        const status = value || row.invoiceStatus || '-';
        const isPaid = row.isPaid;
        const isPartiallyPaid = row.isPartiallyPaid;
        
        return (
          <span className={`px-2 py-1 rounded text-xs ${
            isPaid ? 'bg-green-100 text-green-800' : 
            isPartiallyPaid ? 'bg-yellow-100 text-yellow-800' : 
            'bg-red-100 text-red-800'
          }`}>
            {status}
          </span>
        );
      }
    },
    { 
      key: 'reportType', 
      label: 'Тайлангийн төрөл',
      render: (value: any) => value || '-'
    },
    { 
      key: 'isPaid', 
      label: 'Төлбөр',
      render: (value: any, row: any) => {
        if (value === null || value === undefined) return '-';
        const isPaid = row.isPaid;
        const isPartiallyPaid = row.isPartiallyPaid;
        
        return (
          <span className={`px-2 py-1 rounded text-xs ${
            isPaid ? 'bg-green-100 text-green-800' : 
            isPartiallyPaid ? 'bg-yellow-100 text-yellow-800' : 
            'bg-red-100 text-red-800'
          }`}>
            {isPaid ? 'Төлсөн' : isPartiallyPaid ? 'Хэсэгчлэн' : 'Төлөөгүй'}
          </span>
        );
      }
    },
  ];

  useEffect(() => {
    // Year эсвэл Customer өөрчлөгдөх үед дахин ачаалах
    fetchInsuranceHistories();
  }, [fetchInsuranceHistories]);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">НДШ тайлангийн түүх</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        {/* Year and Customer Selection with Total Customers */}
        <div className="mb-4 flex items-center gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[150px]">
            <div className="text-sm text-gray-600">Нийт харилцагч</div>
            <div className="text-2xl font-bold text-gray-800">
              {insuranceHistories.length}
            </div>
          </div>
          <div className="flex-1 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Тайлант жил сонгох (Бүх харилцагчийн НДШ тайлангийн түүх)
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Харилцагч сонгох
              </label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="Бүгд">Бүгд</option>
                {customers.map((customer) => (
                  <option key={customer.customerOid} value={customer.customerOid}>
                    {customer.customerName} ({customer.customerID})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Уншиж байна...</p>
          </div>
        )}

        {!loading && (
        <SmartTable
            data={flattenMonthsData()}
            columns={getColumns()}
            fetchData={fetchInsuranceHistories}
            settingsKey="insurance-history-table"
          editable={false}
          addable={false}
            rowKey="_rowKey"
          showColumnSearch={showColumnSearch}
          showColumnSettings={showColumnSettings}
          footerData={calculateFooterData()}
        />
        )}

        <ErrorPopup
          isVisible={showErrorPopup}
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />
        
        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default InsuranceGetHistoryReport;